import React, { useEffect, useState } from 'react';
import { Box, Tooltip, Typography, useMediaQuery } from '@mui/material';
import {
    CustomAvatar,
    InformationChip,
    ItemActions,
    useL2DTheme,
} from '@europrocurement/flexy-components';
import { enqueueSnackbar } from 'notistack';
import { dateToFrenchFormat } from '@europrocurement/l2d-utils';
import { useModalMail } from '@europrocurement/l2d-modules/modules/MailForm';
import { useActionLogger } from '../../../../../hooks';
import DocumentIcon from '../DocumentIcon/DocumentIcon';

export type InvoiceStatus = 'paid' | 'pending' | 'draft';

export type InvoiceItemDetailsProps = {
    createdBy?: string;
    createdAt?: string;
    amount?: string;
};

export type InvoiceItemProps = {
    label: string;
    status: InvoiceStatus;
    title?: string;
    feedback?: string;
    id?: number;
    onDownload?: (fileName: string, id: number) => Promise<void>;
    onShow?: (fileName: string, id: number) => Promise<void>;
    validateInvoice?: () => void;
    deleteInvoice?: () => void;
} & InvoiceItemDetailsProps;

const getColorByStatus = (status: InvoiceStatus) => {
    if (status === 'draft') {
        return 'secondary';
    }
    if (status === 'paid') {
        return 'success';
    }

    return 'error';
};

const InvoiceItem: React.FunctionComponent<InvoiceItemProps> = function (props) {
    const {
        id,
        label,
        feedback,
        createdBy,
        createdAt,
        amount,
        onDownload,
        onShow,
        validateInvoice,
        deleteInvoice,
        status = 'pending',
        title = 'le document',
    } = props;
    const color = getColorByStatus(status);
    const { palette, breakpoints } = useL2DTheme();
    const downMd = useMediaQuery(breakpoints.down('md'));
    const [hover, setHover] = useState<boolean>(false);
    const baseColor = palette[color].main;

    const { resolveUserInfo: resolveUserInfoFn } = useActionLogger();
    const { showMailModal } = useModalMail();
    const [userInfo, setUserInfo] = useState<{
        firstName?: string;
        lastName?: string;
        email?: string;
    }>({});
    const resolveUserInfoRef = React.useRef(resolveUserInfoFn);
    resolveUserInfoRef.current = resolveUserInfoFn;

    useEffect(() => {
        if (!createdBy) return undefined;

        let cancelled = false;
        resolveUserInfoRef
            .current(createdBy)
            .then((user) => {
                if (!cancelled && user) {
                    setUserInfo({
                        firstName: user.firstName as string,
                        lastName: user.lastName as string,
                        email: user.email as string,
                    });
                }
            })
            .catch(() => {});

        return () => {
            cancelled = true;
        };
    }, [createdBy]);

    const handleContactClick = () => {
        const { email } = userInfo;
        if (!email) {
            enqueueSnackbar('Email non disponible pour cet utilisateur', {
                variant: 'warning',
            });
            return;
        }

        showMailModal({
            to: [
                {
                    emailAddress: email,
                    firstName: userInfo.firstName || '',
                    lastName: userInfo.lastName || '',
                },
            ],
            subject: '',
            showSubject: true,
            showBody: true,
        });
    };

    const handleMouseEnter = () => setHover(true);
    const handleMouseLeave = () => setHover(false);

    const handleDownload = async () => {
        if (!id || !onDownload) return;
        await onDownload(`${label}.pdf`, id).catch(() =>
            enqueueSnackbar('Une erreur est survenue lors du téléchargement du document', {
                variant: 'error',
            }),
        );
    };

    const handleShow = async () => {
        if (!id || !onShow) return;
        await onShow(`${label}.pdf`, id).catch(() =>
            enqueueSnackbar('Une erreur est survenue lors du téléchargement du document', {
                variant: 'error',
            }),
        );
    };

    const wrapperSx = {
        display: 'flex',
        gap: '0 8px',
        alignItems: 'center',
        border: `solid 1px ${palette.grey[300]}`,
        borderRadius: '12px',
        padding: '8px',
        transition: '0.1s ease',
        boxShadow: hover ? `0px 2px 2.5px 0px ${palette.grey[200]}` : 'none',
        cursor: 'pointer',
    };

    const avatarWrapperSx = {
        alignSelf: downMd ? 'flex-start' : 'auto',
        padding: '10px 8px',
    };

    const infoWrapperSx = {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        flex: 1,
    };

    const nameSx = {
        color: palette.primary.dark,
        fontSize: '14px',
        lineHeight: '17px',
        fontWeight: '500',
    };

    const inlinedWrapperSx = {
        display: 'flex',
        flexDirection: downMd ? 'column' : 'row',
        alignItems: downMd ? 'flex-start' : 'center',
        gap: downMd ? '4px 0' : '0 8px',
    };

    const actionWrapperSx = {
        display: 'flex',
        padding: '0 6.5px',
        gap: '0 12px',
        alignItems: 'center',
        marginLeft: 'auto',
    };

    const textStyle = {
        fontSize: '12px',
        fontWeight: '400',
        lineHeight: '16px',
        color: palette.grey[500],
    };

    const amountStyle = {
        fontSize: '13px',
        fontWeight: '600',
        lineHeight: '16px',
        color: 'text.primary',
    };

    return (
        <Box
            sx={wrapperSx}
            data-testid="invoice-item"
            onMouseEnter={() => handleMouseEnter()}
            onMouseLeave={() => handleMouseLeave()}
        >
            <Box sx={avatarWrapperSx}>
                <DocumentIcon
                    color={color}
                    icon="file-pdf"
                />
            </Box>
            <Box sx={infoWrapperSx}>
                <Box sx={inlinedWrapperSx}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '0 4px' }}>
                        <Typography
                            component="span"
                            sx={nameSx}
                        >
                            {label}
                        </Typography>
                    </Box>
                </Box>
                <Box sx={inlinedWrapperSx}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '0 6px' }}>
                        {createdBy ? (
                            <Tooltip
                                title={
                                    userInfo.firstName && userInfo.lastName
                                        ? `${userInfo.firstName} ${userInfo.lastName}`
                                        : createdBy
                                }
                            >
                                <Box>
                                    <CustomAvatar
                                        keycloakId={createdBy}
                                        firstName={userInfo.firstName}
                                        lastName={userInfo.lastName}
                                        sx={{ width: '20px', height: '20px', cursor: 'pointer' }}
                                        onClick={handleContactClick}
                                    />
                                </Box>
                            </Tooltip>
                        ) : null}
                    </Box>
                    {createdAt ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography
                                sx={textStyle}
                                component="span"
                            >
                                {dateToFrenchFormat(new Date(createdAt))}
                            </Typography>
                        </Box>
                    ) : null}
                    {amount ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography
                                sx={amountStyle}
                            >{`${Number(amount).toFixed(2)} €`}</Typography>
                        </Box>
                    ) : null}
                    {feedback ? (
                        <InformationChip
                            color={color}
                            label={
                                <Typography
                                    variant="l2dtableCell"
                                    color={baseColor}
                                >
                                    {feedback}
                                </Typography>
                            }
                        />
                    ) : null}
                </Box>
            </Box>
            <Box sx={actionWrapperSx}>
                <ItemActions
                    onValidate={validateInvoice ?? undefined}
                    validateTooltip="Valider la facture"
                    onShow={id && onShow ? handleShow : undefined}
                    showTooltip={`Voir ${title}`}
                    onDownload={id && onDownload ? handleDownload : undefined}
                    downloadTooltip={`Télécharger ${title}`}
                    onDelete={deleteInvoice ?? undefined}
                    deleteTooltip={status === 'draft' ? 'Supprimer la facture' : 'Créer un avoir'}
                    sx={{
                        scale: '1.5',
                        marginRight: '20px',
                    }}
                />
            </Box>
        </Box>
    );
};

export default InvoiceItem;
